import { AskRequest, AskResponse, ChatRequest } from "./models";

// Helper function to get the correct API URL
// In development: uses relative path (goes through Vite proxy)
// In production: uses full URL from environment variable
function getApiUrl(path: string): string {
    const isDev = import.meta.env.DEV;

    if (isDev) {
        // Development → Vite proxy
        return path;
    }

    // Production → env value
    const baseUrl = import.meta.env.VITE_CHATBOT_API;
    return `${baseUrl}${path}`;
}

export async function askApi(options: AskRequest): Promise<AskResponse> {
    const url = getApiUrl("/ask");
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            question: options.question,
            approach: options.approach,
            overrides: {
                retrieval_mode: options.overrides?.retrievalMode,
                semantic_ranker: options.overrides?.semanticRanker,
                semantic_captions: options.overrides?.semanticCaptions,
                top: options.overrides?.top,
                temperature: options.overrides?.temperature,
                prompt_template: options.overrides?.promptTemplate,
                prompt_template_prefix: options.overrides?.promptTemplatePrefix,
                prompt_template_suffix: options.overrides?.promptTemplateSuffix,
                exclude_category: options.overrides?.excludeCategory

            }
        })
    });

    const parsedResponse: AskResponse = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse;
}

export async function chatApi(options: ChatRequest): Promise<Response> {
    // Azure API uses /api/v1/conversation
    const path = "/api/v1/conversation";
    const url = getApiUrl(path);

    // Extract the last user message from history
    const lastMessage = options.history.length > 0
        ? options.history[options.history.length - 1].user
        : "";

    // Build headers with token
    const headers: Record<string, string> = {
        "accept": "application/json",
        "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (options.token) {
        headers["Authorization"] = `Bearer ${options.token}`;
    }

    // Build request body with message and optional conversation_id
    const requestBody: any = {
        message: lastMessage
    };

    // Include conversation_id: send null if it doesn't exist
    requestBody.conversation_id = options.conversationId || null;

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
    });
}

export async function update(data: any, token?: string | null): Promise<Response> {
    const url = getApiUrl("/update");

    // Build headers with optional token
    const headers: Record<string, string> = {
        "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data)
    });
}

export async function userFeedback(messageId: string, feedback: number, token?: string | null): Promise<Response> {
    const url = getApiUrl("/api/v1/conversation/userfeedback");

    // Build headers with token
    const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
            message_id: messageId,
            userfeedback: feedback
        })
    });
}

export async function endConversationApi(conversationId: string, token?: string | null): Promise<Response> {
    const url = getApiUrl("/api/v1/conversation/end");

    // Build headers with token
    const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
            conversation_id: conversationId
        })
    });
}

export function getCitationFilePath(citation: string): string {
    return `${citation}`;
    // return `2018-2020_Environmental_KPI_Metrics-0.pdf`;
}

export async function consentApi(conversationId: string, consent: boolean, token?: string | null): Promise<Response> {
    const url = getApiUrl("/api/v1/consent");

    // Build headers with token
    const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json"
    };

    // Add Authorization header if token exists
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
            conversation_id: conversationId,
            consent: consent
        })
    });
}

export async function revokeConsentApi(conversationId: string, revokeConsent: boolean, deleteData: boolean, token?: string | null): Promise<Response> {
    const url = getApiUrl("/api/v1/revokeconsent");

    // Build headers with token
    const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json"
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
            conversation_id: conversationId,
            revokeconsent: revokeConsent,
            deletedata: deleteData
        })
    });
}
